import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { historicalConversionService } from '../services';
import { getApiError } from '@/lib/utils';

// User-facing step labels — no stage numbers, no internal route terms.
const STEP_LABELS = [
  'Setting up opening stock',
  'Importing deliveries and credits',
  'Importing sales records',
  'Importing period totals',
  'Reconciling closing stock',
  'Reconciling bar takings',
  'Processing adjustments',
  'Running calculation',
];

const POLL_INTERVAL_MS = 3500;

export default function PsbPipelineProgressPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const s1RunId = searchParams.get('s1_run_id');
  // back_s1: if set, Back goes to Stage 1B review for that run; else goes to import landing.
  const backS1 = searchParams.get('back_s1');
  const backTarget = backS1
    ? `/stocktaking/psb-stage1b-mapping?s1_run_id=${backS1}`
    : '/stocktaking/import';

  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [startError, setStartError] = useState(null);
  const [started, setStarted] = useState(false);
  const pollRef = useRef(null);

  // Start pipeline on mount — but ONLY when there is no existing pipeline row
  // for this run at all (a brand new import that has never been queued).
  // iter-recovery-B — previously this only skipped re-triggering for status
  // 'complete'; every other status (queued/running/review_required/error)
  // still called startPipeline again on every reopen of this page, silently
  // resetting/duplicating in-flight or already-finished work. Reopening this
  // page must only ever show current status — starting or retrying the
  // pipeline now requires an explicit user action (the "Retry import" button).
  useEffect(() => {
    if (!s1RunId) return;
    let cancelled = false;

    (async () => {
      try {
        // getPipelineStatus returns HTTP 200 with status:'not_started' when no
        // row exists yet (it only 404s for an unknown/foreign s1_run_id) — it
        // does NOT throw for "no row". Treat 'not_started' exactly like "no
        // row found": that is the one case that is safe (and necessary) to
        // auto-start. Any other error (network blip, 5xx) must NOT fall
        // through to starting the pipeline — only a genuine 404 means "ask
        // the backend to create the row".
        let existing = null;
        let is404 = false;
        try {
          existing = await historicalConversionService.getPipelineStatus(s1RunId);
        } catch (statusErr) {
          is404 = statusErr?.response?.status === 404;
          if (!is404) {
            // Transient/unexpected error — show it, do NOT start/retrigger.
            if (!cancelled) setStartError(getApiError(statusErr, 'Could not load import status. Please try again.'));
            return;
          }
        }

        if (existing && existing.status !== 'not_started') {
          // A pipeline row already exists in a real (non-'not_started') status
          // — never auto re-trigger. Just show it and let the poller below take over.
          if (!cancelled) { setPipelineStatus(existing); setStarted(true); }
          return;
        }

        // Either a genuine 404, or a 'not_started' row — this is a brand new
        // import that has never been queued. Safe (and required) to start.
        await historicalConversionService.startPipeline(s1RunId);
        if (!cancelled) setStarted(true);
      } catch (err) {
        if (!cancelled) setStartError(getApiError(err, 'Could not start the import. Please try again or contact support.'));
      }
    })();

    return () => { cancelled = true; };
  }, [s1RunId]);

  // Poll status once pipeline is queued/running.
  useEffect(() => {
    if (!s1RunId || startError) return;

    const poll = async () => {
      try {
        const data = await historicalConversionService.getPipelineStatus(s1RunId);
        setPipelineStatus(data);
        if (data.status === 'complete' || data.status === 'error') {
          clearInterval(pollRef.current);
        }
      } catch {
        // keep polling — transient network error
      }
    };

    // Poll immediately then on interval.
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [s1RunId, startError, started]);

  const pct = pipelineStatus?.progress_pct ?? 0;
  const status = pipelineStatus?.status ?? (started ? 'queued' : 'starting');
  const currentLabel = pipelineStatus?.current_label ?? 'Preparing your import…';
  const sessionId = pipelineStatus?.session_id;
  const errorMsg = pipelineStatus?.error_message || startError;

  const isComplete = status === 'complete';
  const isReviewRequired = status === 'review_required';
  const isError = status === 'error' || !!startError;
  const isRunning = !isComplete && !isReviewRequired && !isError;

  // Derive which step index is active (0-based) from stages_done reported by pipeline.
  const stagesCount = 8;
  const stepsDone = pipelineStatus
    ? Math.min(Math.round((pct / 100) * stagesCount), stagesCount)
    : 0;

  if (!s1RunId) {
    return (
      <div className="max-w-xl mx-auto mt-12 space-y-4">
        <p className="text-sm text-red-600">No import run specified. Please go back and try again.</p>
        <Button variant="outline" onClick={() => navigate('/stocktaking/import')}>
          Back to imports
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="psb-pipeline-progress-page">
      {/* Back nav */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(backTarget)}
          data-testid="pipeline-back-btn"
          title="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">
            {isComplete ? 'Import complete' : isReviewRequired ? 'Final review needed' : isError ? 'Import paused' : 'Importing your data'}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isComplete
              ? 'Your historical stocktake has been imported successfully.'
              : isReviewRequired
              ? 'All stages have run. One final review step is required to confirm the session.'
              : isError
              ? 'The import paused safely. No data was modified.'
              : 'This runs automatically in the background — you can leave this page and return.'}
          </p>
        </div>
      </div>

      <Card data-testid="pipeline-progress-card">
        <CardContent className="pt-6 pb-5 space-y-5">

          {/* Overall progress bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="pipeline-current-label">{currentLabel}</span>
              <span data-testid="pipeline-pct">{pct}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div
                data-testid="pipeline-progress-bar"
                className={`h-2.5 rounded-full transition-all duration-700 ${
                  isComplete ? 'bg-emerald-500' : isReviewRequired ? 'bg-amber-500' : isError ? 'bg-red-500' : 'bg-blue-500'
                }`}
                style={{ width: `${isRunning && pct === 0 ? 3 : pct}%` }}
              />
            </div>
          </div>

          {/* Step list */}
          <ol className="space-y-2">
            {STEP_LABELS.map((label, i) => {
              const done = i < stepsDone;
              const active = !isComplete && !isError && i === stepsDone;
              return (
                <li
                  key={label}
                  className={`flex items-center gap-2.5 text-sm ${
                    done ? 'text-emerald-700' : active ? 'text-slate-900 font-medium' : 'text-slate-400'
                  }`}
                  data-testid={`pipeline-step-${i}`}
                >
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  ) : active ? (
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin shrink-0" />
                  ) : (
                    <span className="h-4 w-4 rounded-full border border-slate-200 shrink-0 inline-block" />
                  )}
                  {label}
                </li>
              );
            })}
          </ol>

          {/* Error message */}
          {isError && errorMsg && (
            <div
              className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700"
              data-testid="pipeline-error-message"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Review required CTA */}
          {isReviewRequired && (
            <div
              className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 space-y-2"
              data-testid="pipeline-review-required-block">
              <p className="font-medium">All import stages have run successfully.</p>
              <p>A brief final review is required before your stocktake session is confirmed. Open the calculation review, check any flagged items, then confirm acceptance.</p>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 border-amber-400 text-amber-800 hover:bg-amber-100"
                onClick={() => navigate(`/stocktaking/psb-stage7-calc?s1_run_id=${s1RunId}`)}
                data-testid="pipeline-review-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open final review
              </Button>
            </div>
          )}

          {/* Success CTA */}
          {isComplete && sessionId && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 space-y-2"
              data-testid="pipeline-success-block">
              <p className="font-medium">Your historical stocktake session is ready.</p>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => navigate(`/stocktaking/sessions/${sessionId}`)}
                data-testid="pipeline-view-session-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View completed session
              </Button>
            </div>
          )}

          {isComplete && !sessionId && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800"
              data-testid="pipeline-complete-no-session">
              Import complete. You can find the new stocktake session in the Stocktake list.
            </div>
          )}

          {/* Retry on error */}
          {isError && !startError && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                setStartError(null);
                setPipelineStatus(null);
                try {
                  await historicalConversionService.startPipeline(s1RunId);
                } catch (err) {
                  setStartError(getApiError(err, 'Retry failed.'));
                }
              }}
              data-testid="pipeline-retry-btn"
            >
              Retry import
            </Button>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        The import runs securely in the background. All product decisions you made in the
        review step are preserved. You can return to this page at any time.
      </p>
    </div>
  );
}
